import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import InventoryLayout from "@/components/inventory/InventoryLayout";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const tiles = [
  { to: "/inventory/products", label: "Products", key: "products_active", of: "products_total", testid: "tile-products" },
  { to: "/inventory/warehouses", label: "Warehouses", key: "warehouses_active", of: "warehouses_total", testid: "tile-warehouses" },
  { to: "/inventory/categories", label: "Categories", key: "categories_total", testid: "tile-categories" },
  { to: "/inventory/movements", label: "Movements", key: "movements_total", testid: "tile-movements" },
];

export default function InventoryHome() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/inventory/summary");
        setSummary(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <InventoryLayout title="Overview">
      <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#002FA7] mb-6">
        Sprint 3 · Inventory · v0.4.0
      </p>

      <section
        data-testid="inv-summary-grid"
        className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-slate-200 bg-white"
      >
        {tiles.map((t) => (
          <Link
            key={t.to}
            to={t.to}
            data-testid={t.testid}
            className="p-6 border-r border-b border-slate-200 last:border-r-0 hover:bg-slate-50 transition-colors"
          >
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 mb-3">
              {t.label}
            </div>
            <div className="text-3xl font-semibold tracking-tight">
              {loading ? "—" : summary?.[t.key] ?? 0}
              {t.of && summary && (
                <span className="text-sm text-slate-500 ml-2 font-normal">
                  / {summary[t.of]}
                </span>
              )}
            </div>
          </Link>
        ))}
      </section>

      <section className="mt-12">
        <div className="flex items-center justify-between mb-6">
          <h2
            style={{ fontFamily: "'Outfit', sans-serif" }}
            className="text-2xl font-medium tracking-tight"
          >
            Low stock
          </h2>
          <span
            data-testid="inv-low-stock-count"
            className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500"
          >
            {summary?.low_stock?.length ?? 0} items
          </span>
        </div>

        {loading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : summary?.low_stock?.length ? (
          <div className="border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left">
                  <th className="px-4 py-3 text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">SKU</th>
                  <th className="px-4 py-3 text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Name</th>
                  <th className="px-4 py-3 text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">On hand</th>
                  <th className="px-4 py-3 text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Reorder</th>
                </tr>
              </thead>
              <tbody data-testid="inv-low-stock-tbody">
                {summary.low_stock.map((row) => (
                  <tr key={row.product_id} className="border-b border-slate-100 last:border-b-0">
                    <td className="px-4 py-3 font-mono text-xs">{row.sku}</td>
                    <td className="px-4 py-3">{row.name}</td>
                    <td className="px-4 py-3 text-[#D92D20] font-medium">{row.on_hand}</td>
                    <td className="px-4 py-3 text-slate-600">{row.reorder_level}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p
            data-testid="inv-no-low-stock"
            className="text-sm text-slate-500 border border-dashed border-slate-300 p-6 text-center"
          >
            No low-stock alerts. All active products are at or above their reorder level.
          </p>
        )}
      </section>

      <section className="mt-12 text-xs uppercase tracking-[0.25em] text-slate-500">
        <a
          href={`${process.env.REACT_APP_BACKEND_URL}/api/docs#/Inventory`}
          target="_blank"
          rel="noreferrer noopener"
          data-testid="inv-swagger-link"
          className="hover:text-[#002FA7] transition-colors"
        >
          → Inventory API in Swagger
        </a>
      </section>
    </InventoryLayout>
  );
}
